/*
	boxart share artifacts — spoiler-free (spec §10).

	The share is a 4x4 (16-cell) grid filled in GUESS ORDER (NOT tile positions):
	  🟥  a wrong guess
	  🟩  the correct/solving guess (the last filled cell on a win)
	  ⬜  an unused guess slot (stands in for "grey"; there is no true grey
	      square emoji)

	On a win in N guesses: the first N-1 cells are 🟥, the Nth is 🟩, the rest ⬜.
	On a loss: all 16 cells are 🟥 (16 wrong guesses, board exhausted).

	Neither artifact shows the box art or the game name, so a result can be posted
	without spoiling the puzzle for anyone who hasn't played yet.

	The `result` passed in is the store's game.result enriched by the End screen
	with `guesses` (picks made) and `tiles` (tiles shown when solved). Shape:
	  { solved, lost, guesses, tiles, score, answer }
*/

var COLS = 4
var ROWS = 4
var CELLS = COLS * ROWS // 16

var SQUARE_WRONG = '🟥'
var SQUARE_CORRECT = '🟩'
var SQUARE_EMPTY = '⬜'

// canvas palette — softened squares to match the design card (not pure emoji red)
var PAPER = '#fdfbf6'
var INK = '#1c1a16'
var MUTED = '#9a9284'
var CELL_WRONG = '#bf6a59'
var CELL_CORRECT = '#6f9168'
var CELL_EMPTY = '#ece5d7'
var CELL_EMPTY_BORDER = '#e0d8c8'

/*
	Resolve the per-cell state array (length 16) in guess order.
	state: 'wrong' | 'correct' | 'empty'
*/
function cells(result) {

	result = result || {}

	var solved = !!result.solved
	var guesses = Math.max(0, Math.min(CELLS, result.guesses || 0))

	var out = []

	for (var i = 0; i < CELLS; i++) {

		if (i < guesses - 1) {

			out.push('wrong')

		} else if (i === guesses - 1) {

			// the final guess: green on a solve, red on a loss
			out.push(solved ? 'correct' : 'wrong')

		} else {

			out.push('empty')

		}

	}

	return out

}

/*
	Header line for the text share, e.g.
	  whatboxart #142 - 3 tiles
	  whatboxart #142 - out of guesses, 16 tiles   (loss)
	No em-dashes (UI copy rule), plain hyphens.
*/
function header(result, number) {

	result = result || {}

	var solved = !!result.solved
	var tiles = result.tiles || 0

	var no = (number != null && number !== '') ? number : '?'

	var tileText = tiles + ' tile' + (tiles === 1 ? '' : 's')

	var outcome = solved
		? tileText
		: ('out of guesses, ' + tileText)

	return 'whatboxart #' + no + ' - ' + outcome

}

export default {

	// the 16-cell state array ('wrong' | 'correct' | 'empty') in guess order,
	// for rendering the result grid on screen
	cells: function(result) {

		return cells(result)

	},

	header: function(result, number) {

		return header(result, number)

	},

	/*
		The copy/paste text grid: header line, then 4 rows of 4 squares, filled
		in guess order. Spoiler-free.
	*/
	grid: function(result, number) {

		var state = cells(result)

		var rows = []

		for (var r = 0; r < ROWS; r++) {

			var row = ''

			for (var c = 0; c < COLS; c++) {

				var s = state[r * COLS + c]

				row += s === 'wrong' ? SQUARE_WRONG : (s === 'correct' ? SQUARE_CORRECT : SQUARE_EMPTY)

			}

			rows.push(row)

		}

		return header(result, number) + '\n' + rows.join('\n')

	},

	/*
		A canvas render of the SAME result grid plus the header + score, on the
		paper ground in the editorial style. Spoiler-free: no art, no name.
		Resolves to a PNG Blob.
	*/
	image: function(result, number) {

		var state = cells(result)

		// layout (device-independent units, then scaled for crispness)
		var scale = (typeof window !== 'undefined' && window.devicePixelRatio) ? Math.min(window.devicePixelRatio, 3) : 2

		var W = 440
		var pad = 40
		var gap = 12
		var gridTop = 120
		var gridW = W - pad * 2
		var cell = (gridW - gap * (COLS - 1)) / COLS

		// size the canvas to the grid so nothing overlaps it: grid bottom + a
		// matching bottom margin. (No footer — branding is the wordmark up top.)
		var gridBottom = gridTop + ROWS * cell + (ROWS - 1) * gap
		var H = Math.round(gridBottom + pad)

		var canvas = document.createElement('canvas')
		canvas.width = W * scale
		canvas.height = H * scale

		var ctx = canvas.getContext('2d')
		ctx.scale(scale, scale)

		// paper ground
		ctx.fillStyle = PAPER
		ctx.fillRect(0, 0, W, H)

		// wordmark (Newsreader display) top-left
		ctx.textBaseline = 'alphabetic'
		ctx.textAlign = 'left'
		ctx.fillStyle = INK
		ctx.font = '600 30px "Newsreader", Georgia, serif'
		ctx.fillText('whatboxart', pad, 70)

		// number + score top-right (mono)
		var no = (number != null && number !== '') ? number : '?'
		var scoreLabel = (result && result.score != null) ? ('Score ' + result.score) : ''
		ctx.textAlign = 'right'
		ctx.fillStyle = MUTED
		ctx.font = '13px "IBM Plex Mono", monospace'
		ctx.fillText('#' + no + (scoreLabel ? ('  ·  ' + scoreLabel) : ''), W - pad, 66)

		// outcome subline (left, mono caps) — no "solved" word; tiles on a win
		var solved = !!(result && result.solved)
		var tiles = (result && result.tiles) || 0
		ctx.textAlign = 'left'
		ctx.fillStyle = solved ? CELL_CORRECT : CELL_WRONG
		ctx.font = '12px "IBM Plex Mono", monospace'
		ctx.fillText(solved ? (tiles + ' TILE' + (tiles === 1 ? '' : 'S')) : 'OUT OF GUESSES', pad, 96)

		// the 4x4 grid
		for (var i = 0; i < CELLS; i++) {

			var col = i % COLS
			var row = Math.floor(i / COLS)
			var x = pad + col * (cell + gap)
			var y = gridTop + row * (cell + gap)
			var s = state[i]

			ctx.fillStyle = s === 'wrong' ? CELL_WRONG : (s === 'correct' ? CELL_CORRECT : CELL_EMPTY)
			roundRect(ctx, x, y, cell, cell, 6)
			ctx.fill()

			if (s === 'empty') {

				ctx.lineWidth = 1
				ctx.strokeStyle = CELL_EMPTY_BORDER
				roundRect(ctx, x + 0.5, y + 0.5, cell - 1, cell - 1, 6)
				ctx.stroke()

			}

		}

		return new Promise(function(resolve) {

			if (canvas.toBlob) {

				canvas.toBlob(function(blob) {

					resolve(blob)

				}, 'image/png')

			} else {

				// very old fallback: derive a Blob from the data URL
				var data = canvas.toDataURL('image/png')
				var binary = atob(data.split(',')[1])
				var len = binary.length
				var bytes = new Uint8Array(len)

				for (var j = 0; j < len; j++) bytes[j] = binary.charCodeAt(j)

				resolve(new Blob([bytes], { type: 'image/png' }))

			}

		})

	}

}

// rounded-rect path helper (older canvas impls lack ctx.roundRect)
function roundRect(ctx, x, y, w, h, r) {

	r = Math.min(r, w / 2, h / 2)

	ctx.beginPath()
	ctx.moveTo(x + r, y)
	ctx.arcTo(x + w, y, x + w, y + h, r)
	ctx.arcTo(x + w, y + h, x, y + h, r)
	ctx.arcTo(x, y + h, x, y, r)
	ctx.arcTo(x, y, x + w, y, r)
	ctx.closePath()

}
